import { Schema } from 'effect';
import { SiteId } from './common.js';
/**
 * Request/response DTO schemas for the /event group.
 *
 * Event and message documents are passed through as stored (`Schema.Any`) —
 * they are wide, versioned shapes owned by `toEventDoc` & friends in the shared
 * package, and pinning them here would silently drop fields.
 */
export const EventParams = Schema.Struct({
    site: SiteId,
    eventId: Schema.String
});
export const MessageEmailParams = Schema.Struct({
    site: SiteId,
    eventId: Schema.String,
    messageId: Schema.String
});
export const FeedbackTokenParams = Schema.Struct({
    site: SiteId,
    eventId: Schema.String,
    token: Schema.String
});
/**
 * The SiteContext the frontend sends so email links match the host the admin is
 * on. Absent or malformed → the template falls back to defaults, so every field
 * is optional and the handler does the final shape check.
 */
export const SiteContextBody = Schema.Struct({
    siteContext: Schema.optional(Schema.Any)
});
// --- POST /event/:site/newevent ----------------------------------------------
export const NewEventParams = Schema.Struct({ site: SiteId });
export const NewEventResponse = Schema.Struct({ eventId: Schema.String });
// --- POST /event/:site/:eventId/messages/:messageId/email --------------------
export const MessageEmailResponse = Schema.Struct({
    queued: Schema.Number,
    reason: Schema.optional(Schema.String)
});
// --- GET /event/:site/:eventId/participants ----------------------------------
export const ParticipantsResponse = Schema.Struct({
    uids: Schema.mutable(Schema.Array(Schema.String)),
    emails: Schema.mutable(Schema.Array(Schema.String))
});
// --- GET /event/:site/:eventId/partakers -------------------------------------
/**
 * The published start list, for callers who are NOT bookie-or-higher on the
 * hosting site. Pinned field-by-field on purpose: this is the one response in
 * the group that must never grow a field by accident, because the documents it
 * is derived from carry ownerUid, orderId and every free-form answer
 * (F-RULES-01). Schema.Any here would defeat the projection.
 *
 * `published: false` means the organizer has not published the list, or it has
 * aged out — `regs` is then empty rather than the request failing, so the
 * frontend renders the same "not published" state it always has.
 */
export const PartakerTicket = Schema.Struct({
    ticketId: Schema.String,
    partakerId: Schema.String,
    handlerName: Schema.String,
    dogName: Schema.String,
    extra: Schema.Record(Schema.String, Schema.Any)
});
export const PartakerReg = Schema.Struct({
    eventId: Schema.String,
    eventSubId: Schema.String,
    tickets: Schema.mutable(Schema.Array(PartakerTicket))
});
/**
 * Seat availability, per sub-event. Returned even when the start list is NOT
 * published, because it is not part of the disclosure decision: it is a count,
 * the event document already carries an approximation of it
 * (`subs[].tickets.reserved`), and the sold-out state is public anyway. Without
 * it the signup page derives "N ledige" from `regs` and, now that non-organizers
 * cannot read those, would advertise free seats on a full course and fail at
 * checkout. Counts reserved + booked, matching eventTicketsBookedOrReserved.
 */
export const PartakerCount = Schema.Struct({
    eventSubId: Schema.String,
    bookedOrReserved: Schema.Number
});
export const PartakersResponse = Schema.Struct({
    published: Schema.Boolean,
    // Whether the caller holds a booked ticket on this event, and therefore saw
    // the EventFormAudience.participant projection rather than the public one.
    // Named to match PrivateMessagesResponse, which gates on the same rule.
    isParticipant: Schema.Boolean,
    reason: Schema.String,
    regs: Schema.mutable(Schema.Array(PartakerReg)),
    counts: Schema.mutable(Schema.Array(PartakerCount))
});
// --- GET /event/:site/:eventId/messages/private ------------------------------
export const PrivateMessagesResponse = Schema.Struct({
    isParticipant: Schema.Boolean,
    messages: Schema.mutable(Schema.Array(Schema.Any))
});
// --- POST /event/:site/:eventId/feedback/send --------------------------------
export const FeedbackSendResponse = Schema.Struct({
    queued: Schema.Number,
    skipped: Schema.Number,
    reason: Schema.optional(Schema.String)
});
// --- GET /event/:site/:eventId/feedback --------------------------------------
export const FeedbackListResponse = Schema.Struct({
    feedbacks: Schema.mutable(Schema.Array(Schema.Any))
});
// --- GET/POST /event/:site/:eventId/feedback/:token --------------------------
export const FeedbackInviteResponse = Schema.Struct({
    event: Schema.Struct({
        id: Schema.String,
        name: Schema.String,
        tagLine: Schema.String,
        feedbackForm: Schema.Any
    }),
    feedback: Schema.Struct({
        submittedAt: Schema.String,
        isAnonymous: Schema.Boolean,
        responses: Schema.Any,
        expiresAt: Schema.String
    })
});
export const FeedbackSubmitBody = Schema.Struct({
    responses: Schema.optional(Schema.Any),
    isAnonymous: Schema.optional(Schema.Any)
});
export const OkResponse = Schema.Struct({ ok: Schema.Boolean });
//# sourceMappingURL=event.js.map